import React from 'react';
import {
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Grid,
	Radio,
	RadioGroup
} from '@mui/material';

import { BooleanField, Form, useSettings } from 'app/main/components';
import Filter from 'app/main/components/filter/Filter';
import type { FiltersType } from 'app/main/components/hooks/useFilters';
import ModuleFilterItem from 'app/main/components/filter/filter-types/ModuleFilterItem';

type DemsRecipientsImportFiltersDialogProps = Pick<
	FiltersType,
	'currentFilters' | 'defaultFilters' | 'handleFilterChange' | 'setCurrentFilters'
> & {
	uid: string;
	table: string;
	srcModule: string;
	setSrcModule: React.Dispatch<React.SetStateAction<string>>;
	srcUid?: string;
	setSrcUid: React.Dispatch<React.SetStateAction<string | undefined>>;
	includePeople?: boolean | null;
	setIncludePeople?: React.Dispatch<React.SetStateAction<boolean | null>>;
	showResults: VoidFunction;
	closeDialog: VoidFunction;
};

export default function DemsRecipientsImportFiltersDialog(props: DemsRecipientsImportFiltersDialogProps) {
	const { settings, t } = useSettings(props.srcModule);

	const rowModule =
		props.srcModule === 'dems'
			? 'dems_recipients'
			: props.srcModule === 'marketingactions'
				? 'marketingactions_rows'
				: 'commercialcampaigns_rows';

	const canIncludePeople = ['commercialcampaigns', 'marketingactions'].includes(props.srcModule);
	const hasFilters = props.table !== 'mails';

	function handleClear() {
		props.setCurrentFilters([]);
	}

	return (
		<>
			<DialogTitle>{t('DEMS_SECTION_COPY_TITLE')}</DialogTitle>
			<DialogContent>
				<Grid container flexDirection="column" spacing={2}>
					<Grid size={12}>
						<RadioGroup
							className="p-4"
							row
							name="srcModule"
							value={props.srcModule}
							onChange={(_e, value) => props.setSrcModule(value)}
						>
							<FormControlLabel
								value="marketingactions"
								control={<Radio color="primary" />}
								label={t('DEMS_SECTION_COPY_MARKETINGACTION')}
							/>
							<FormControlLabel
								value="commercialcampaigns"
								control={<Radio color="primary" />}
								label={t('DEMS_SECTION_COPY_COMMERCIALCAMPAIGN')}
							/>
							<FormControlLabel
								value="dems"
								control={<Radio color="primary" />}
								label={t('DEMS_SECTION_COPY_DEM')}
							/>
						</RadioGroup>
						<ModuleFilterItem
							name={`${settings.modu_short}_uid`}
							operator="equals"
							module={settings.modu_title}
							uid={props.srcUid}
							filters={[{ filt_field: 'dems_uid', filt_clause: 'not_equal', filt_value: props.uid }]}
							sendChange={newValue => props.setSrcUid(newValue)}
						/>
						{canIncludePeople && (
							<BooleanField
								name="include_associated_people"
								label={t('DEMS_SECTION_COPY_INCLUDE_ASSOCIATED_PEOPLE')}
								value={props.includePeople}
								isEdit
								sendChange={(_name, newValue) => props.setIncludePeople?.(newValue)}
							/>
						)}
					</Grid>
					<Grid size={12}>
						{hasFilters && (
							<Form id="demsRecipientsImportFilterForm" onSubmit={props.showResults}>
								<Filter
									module={rowModule}
									currentFilters={props.currentFilters}
									defaultFilters={props.defaultFilters}
									handleFilterChange={props.handleFilterChange}
								/>
							</Form>
						)}
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button onClick={props.closeDialog}>{t('DIALOG_ACTION_CANCEL')}</Button>
				<Button onClick={handleClear}>{t('DIALOG_ACTION_CLEAR')}</Button>
				<Button type="submit" form="demsRecipientsImportFilterForm" color="secondary" disabled={!props.srcUid}>
					{t('DIALOG_ACTION_NEXT')}
				</Button>
			</DialogActions>
		</>
	);
}
